/**
 * @fileoverview
 * ## MetaTagsService
 *
 * Service for managing meta tags data. This service extends the `BackendService` class.
 *
 * @requires `BackendService`, `MetaTagsStore`, `meta-tags.service-worker-model`, `meta-tags.selectors`, `@ngrx/entity`, `rxjs`
 * @provides `MetaTagsService`
 *
 * @usageNotes
 * The `MetaTagsService` provides methods for retrieving, creating, updating, and deleting meta tags.
 *
 * ### Example
 *
 * ```typescript
 * import { MetaTagsService } from './path-to-meta-tags.service';
 *
 * constructor(private metaTagsService: MetaTagsService) {}
 *
 * // Retrieve all meta tags
 * this.metaTagsService.getMetaTagKeys().subscribe(tags => console.log(tags));
 * ```
 */

import {map, Observable} from 'rxjs';

import {Injectable} from '@angular/core';
import {BackendService, IGetAMetaTagsResponse, IMetaTag} from '@core/api/data-access';
import {FeatureStoreNames} from '@shared-types';

import {MetaTagsStore} from '../+state';

import IMetaTagsState = MetaTagsStore.IMetaTagsState;

@Injectable({
	providedIn: 'root',
})
export class MetaTagsService extends BackendService<IMetaTagsState> {
	/**
	 * Constructor for the MetaTagsService.
	 */
	constructor() {
		super('MetaTags', {store: {storeKey: FeatureStoreNames.META_TAGS_FEATURE_KEY}});
	}

	/**
	 * Retrieves the list of meta tag keys.
	 *
	 * @param containDisables - If true, only enabled keys are returned.
	 * @returns Observable of the list of meta tag keys.
	 */
	public getMetaTagKeys(containDisables = false): Observable<IMetaTag[]> {
		return this.apiService.get<IGetAMetaTagsResponse>(this.getUrl('list'), {}).pipe(
			map(data => {
				const metaTags = data.metaTags;

				// Filter enabled keys only if containDisables is true
				if (!containDisables) {
					return metaTags.filter(mt => mt.isEnabled);
				}

				return metaTags;
			}),
		);
	}

	/**
	 * Creates a new meta tag.
	 *
	 * @param request - The meta tag data to create.
	 * @returns Observable of the created meta tag.
	 */
	createMetaTag(request: IMetaTag): Observable<IMetaTag> {
		return this.apiService.post<object, IMetaTag>(this.getUrl('metatag'), request).pipe(
			map(() => {
				return request;
			}),
		);
	}

	/**
	 * Deletes a meta tag by key.
	 *
	 * @param key - The key of the meta tag to delete.
	 * @returns Observable with an empty response.
	 */
	deleteMetaTag(key: string): Observable<object> {
		return this.apiService.delete<object>(this.getUrl('metatag'), {key});
	}

	/**
	 * Updates an existing meta tag.
	 *
	 * @param request - The updated meta tag data.
	 * @returns Observable of the updated meta tag.
	 */
	updateMetaTag(request: IMetaTag): Observable<IMetaTag> {
		return this.apiService.put<object, IMetaTag>(this.getUrl('metatag'), request).pipe(
			map(() => {
				return request;
			}),
		);
	}
}
